/**
 * Vertical chat stream — newest at the bottom, opens pinned to the
 * latest, and pages older history in as you scroll up.
 *
 * Scroll model:
 *   - First load: jump to the bottom INSTANTLY (no animation) so the
 *     Mini App opens already at the latest message.
 *   - New message at the bottom: follow it (smooth) only if the user is
 *     already near the bottom — never yank them up from reading history.
 *   - Scroll near the top: request the previous (older) page via
 *     `onLoadOlder`; once it prepends, the viewport is restored so the
 *     content the user was looking at stays put (no jump).
 *
 * Only the loaded pages live in the DOM — history is pulled in chunks,
 * not all at once.
 */
import { useLayoutEffect, useRef } from "react";
import { UserBubble } from "./bubbles/UserBubble";
import { CapturedNoteBubble } from "./bubbles/CapturedNoteBubble";
import { AskAnswerBubble } from "./bubbles/AskAnswerBubble";
import { DigestBubble } from "./bubbles/DigestBubble";
import { ThinkingBubble } from "./bubbles/ThinkingBubble";
import { RelativeTime } from "../components/RelativeTime";
import type { ChatMessage } from "./types";

interface Props {
  messages: ChatMessage[];
  /** True while the very first feed fetch is in flight (no messages yet). */
  isInitialLoading?: boolean;
  /** Older history exists beyond what's loaded. */
  hasMore?: boolean;
  /** An older page is currently being fetched. */
  isLoadingOlder?: boolean;
  /** Request the previous (older) page. */
  onLoadOlder?: () => void;
}

const TOP_TRIGGER_PX = 140; // start loading older when within this of the top
const NEAR_BOTTOM_PX = 90; // "following" threshold

export function MessageStream({
  messages,
  isInitialLoading = false,
  hasMore = false,
  isLoadingOlder = false,
  onLoadOlder,
}: Props): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didInitialScroll = useRef(false);
  const prevLen = useRef(0);
  const nearBottom = useRef(true);
  // Set right before we ask for an older page; consumed after the prepend
  // to restore the viewport so it doesn't jump.
  const restore = useRef<{ height: number; top: number } | null>(null);

  const handleScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (
      el.scrollTop < TOP_TRIGGER_PX &&
      hasMore &&
      !isLoadingOlder &&
      onLoadOlder
    ) {
      restore.current = { height: el.scrollHeight, top: el.scrollTop };
      onLoadOlder();
    }
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const grew = messages.length > prevLen.current;

    if (!didInitialScroll.current && messages.length > 0) {
      // Open already at the latest — instant, no visible scroll.
      el.scrollTop = el.scrollHeight;
      didInitialScroll.current = true;
    } else if (restore.current) {
      // Older page just prepended — keep the user where they were.
      el.scrollTop = el.scrollHeight - restore.current.height + restore.current.top;
      restore.current = null;
    } else if (grew && nearBottom.current) {
      // A new message landed at the bottom and we were following it.
      el.scrollTop = el.scrollHeight;
    }
    prevLen.current = messages.length;
  }, [messages]);

  if (messages.length === 0) {
    if (isInitialLoading) {
      return (
        <div
          className="flex-1 overflow-hidden py-3"
          aria-busy="true"
          aria-label="Loading memory"
        >
          <SkeletonBubble alignRight={false} widthPct={62} />
          <SkeletonBubble alignRight widthPct={48} />
          <SkeletonBubble alignRight={false} widthPct={70} />
        </div>
      );
    }
    return <EmptyChat />;
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto overflow-x-hidden py-2 flex flex-col gap-1"
    >
      {hasMore || isLoadingOlder ? (
        <div className="flex justify-center py-2" aria-hidden={!isLoadingOlder}>
          {isLoadingOlder ? (
            <span className="loader" style={{ width: 18, height: 18 }} />
          ) : (
            <button
              type="button"
              onClick={onLoadOlder}
              className="text-[12px] px-3 py-1 rounded-full active:scale-95 transition-transform"
              style={{
                background: "var(--color-secondary-bg)",
                color: "var(--color-hint)",
              }}
            >
              Load earlier
            </button>
          )}
        </div>
      ) : null}
      {messages.map((m) => (
        <Row key={`${m.kind}:${m.id}`} message={m} />
      ))}
    </div>
  );
}

function EmptyChat(): React.ReactElement {
  return (
    <div className="flex-1 flex items-center justify-center px-6 text-center">
      <div className="max-w-[300px] space-y-3">
        <div
          className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center"
          style={{ background: "var(--color-secondary-bg)" }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              strokeWidth="1.7"
              style={{ color: "var(--color-hint)" }}
            />
            <path
              d="m20 20-3.2-3.2"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              style={{ color: "var(--color-hint)" }}
            />
          </svg>
        </div>
        <p className="text-[15px] font-semibold">Ask your memory</p>
        <p
          className="text-[12px] leading-snug"
          style={{ color: "var(--color-hint)" }}
        >
          Type a question below and I'll search everything you've captured.
          Forward voice / video / text to the bot — answers live here. Try{" "}
          <code className="font-mono">/today</code> or{" "}
          <code className="font-mono">/week</code>.
        </p>
      </div>
    </div>
  );
}

function SkeletonBubble({
  widthPct,
  alignRight,
}: {
  widthPct: number;
  alignRight: boolean;
}): React.ReactElement {
  return (
    <div
      className={`px-3 py-1 flex ${alignRight ? "justify-end" : "justify-start"}`}
    >
      <div
        className="h-8 rounded-[18px] aim-shimmer"
        style={{
          width: `${widthPct}%`,
          maxWidth: "320px",
          background: "var(--color-secondary-bg)",
        }}
      />
    </div>
  );
}

function Row({ message }: { message: ChatMessage }): React.ReactElement {
  switch (message.kind) {
    case "user_note":
      return (
        <UserBubble
          meta={
            message.status === "sending"
              ? "sending…"
              : message.status === "failed"
                ? "failed — tap to retry"
                : null
          }
        >
          <span className="whitespace-pre-wrap">{message.text}</span>
        </UserBubble>
      );
    case "captured_note":
      return <CapturedNoteBubble message={message} />;
    case "ask_query":
      return (
        <UserBubble
          meta={
            message.status === "asking"
              ? "searching memory…"
              : message.status === "failed"
                ? "failed"
                : null
          }
        >
          {message.question}
        </UserBubble>
      );
    case "ask_answer":
      return <AskAnswerBubble message={message} />;
    case "digest":
      return <DigestBubble message={message} />;
    case "system":
      return (
        <div className="px-4 py-1 flex justify-center">
          <span
            className="text-[11px] px-2.5 py-1 rounded-full"
            style={{
              background: "var(--color-secondary-bg)",
              color:
                message.variant === "error"
                  ? "var(--color-destructive)"
                  : "var(--color-hint)",
            }}
            title={new Date(message.createdAt).toLocaleString()}
          >
            {message.text}
          </span>
          {/* Hidden, but exposed to AT users for context. */}
          <span className="sr-only">
            <RelativeTime iso={message.createdAt} />
          </span>
        </div>
      );
    case "pending":
      return <ThinkingBubble message={message} />;
  }
}
